/**
 * Download a content as a file.
 */
const download = async (content: Uint8Array, filename: string) => {
  const blob = new Blob([content.buffer as ArrayBuffer], {
    type: "application/octet-stream",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.download = filename;
  anchor.href = url;
  anchor.target = "_blank";
  anchor.style.display = "none"; // just to be safe!
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/**
 * Read a file to an Uint8Array.
 */
const read = async (file: File): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      resolve(new Uint8Array(readerEvent.target?.result as ArrayBuffer));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Read files from the filesystem and call a callback on each..
 */
const readFiles = async (
  callback: (content: Uint8Array, filename: string) => Promise<void>,
) => {
  return new Promise<void>((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.style.display = "none";
    input.onchange = async () => {
      if (input.files == null) return;
      for (const file of input.files)
        await callback(await read(file), file.name);
      resolve();
    };
    input.onerror = reject;

    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  });
};

/**
 * Compress a binary data to an URL safe string.
 */
const binaryToString = async (
  data: Uint8Array | undefined | null,
): Promise<string | undefined | null> => {
  if (data == null) return;
  const { compress } = await (await import("brotli-wasm")).default;
  const { Base64 } = await import("js-base64");
  const buffer = compress(data);
  return Base64.fromUint8Array(buffer, true);
};

/**
 * Reverse of binaryToString.
 */
const stringToBinary = async (
  data: string | undefined | null,
): Promise<Uint8Array | undefined | null> => {
  if (data == null) return;
  const { decompress } = await (await import("brotli-wasm")).default;
  const { Base64 } = await import("js-base64");
  const buffer = Base64.toUint8Array(data);
  return decompress(buffer);
};

export { download, readFiles, binaryToString, stringToBinary };
